/**
 * Нормализует HEX-цвет геозоны из атрибутов (формат AARRGGBB или RRGGBB)
 * в CSS-цвет, поддерживаемый OpenLayers Stroke/Fill.
 * В начале строки удаляются префиксы "#" или "0x" при наличии.
 * @param {string} value HEX-строка цвета (например, "ff6e6e6e", "FF005CBA", "#00ff00", "0xFF005CBA")
 * @returns {string|null} Цвет в формате "rgba(r, g, b, a)" или null при невалидном значении
 */
export function parseGeoZoneColor(value) {
  if (typeof value !== 'string') return null;

  let hex = value.trim();
  if (!hex) return null;

  // удаляем префикс "#" или "0x"
  hex = hex.replace(/^(#|0x)/i, '');
  if (!/^[0-9a-fA-F]{6}$/.test(hex) && !/^[0-9a-fA-F]{8}$/.test(hex)) return null;

  // для AARRGGBB первые два символа — альфа-канал (AA/255)
  let alpha = 1;
  if (hex.length === 8) {
    alpha = parseInt(hex.slice(0, 2), 16) / 255;
    hex = hex.slice(2);
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Извлекает пользовательские цвета отрисовки геозоны из её атрибутов
 * (заполняются из стилей KML при импорте, см. #13562):
 * - view.line-color — цвет границ контура
 * - view.background-color — цвет заливки
 *
 * Поддерживаются два представления атрибутов:
 * - customAugmentationEx.view — сгруппированный объект, приоритет выше;
 * - customAugmentation.attrs — массив {group, name, value}.
 * @param {object} geoZone Объект геозоны
 * @returns {{strokeColor?: string, fillColor?: string}} Цвета контура и заливки (CSS rgba)
 */
export function getGeoZoneViewColors(geoZone) {
  if (!geoZone) return {};

  const colors = {};

  // вариант 1: сгруппированные атрибуты customAugmentationEx.view
  const ex = geoZone.customAugmentationEx;
  if (ex?.view) {
    const line = parseGeoZoneColor(ex.view['line-color']);
    const background = parseGeoZoneColor(ex.view['background-color']);
    if (line) colors.strokeColor = line;
    if (background) colors.fillColor = background;
  }

  // вариант 2: массив customAugmentation.attrs
  // (используется только если цвет ещё не найден в customAugmentationEx)
  const attrs = geoZone.customAugmentation?.attrs;
  if (Array.isArray(attrs)) {
    attrs.forEach((attr) => {
      const isLine =
        attr?.name === 'view.line-color' || (attr?.group === 'view' && attr?.name === 'line-color');
      const isBackground =
        attr?.name === 'view.background-color' ||
        (attr?.group === 'view' && attr?.name === 'background-color');

      if (isLine && !colors.strokeColor) {
        const line = parseGeoZoneColor(attr?.value);
        if (line) colors.strokeColor = line;
      }
      if (isBackground && !colors.fillColor) {
        const background = parseGeoZoneColor(attr?.value);
        if (background) colors.fillColor = background;
      }
    });
  }

  return colors;
}
